/*
 * My pay: a person's own pay, week by week (Paid, You owe, Not paid yet, This week so far), and one week's jobs, tips and
 * hours. Only their own share, never a job's price or anyone else's pay. In Spanish when their language is Spanish.
 * ClockCard is the clock in and out button, on My day and My pay.
 */
import { fillWords, payWords, type MyPayAnswer, type MyPayList } from "@steward/shared";
import { useState } from "react";
import { ApiError } from "../api/client";
import { useClock, useMyPay, useMyPayWeek, usePressClock } from "../api/payroll";
import { useReferLink } from "../api/hooks";
import { useSession } from "../app/session";
import { minutesWords, moneyExact } from "../components/format";
import { useToast } from "../components/overlays";
import { Banner, Button, Card, EmptyState, LoadError, Page, Skeleton, StatusLabel, type Status } from "../components/parts";

const dateIn = (locale: string) => (d: string) =>
  new Intl.DateTimeFormat(locale === "es" ? "es-US" : "en-US", { timeZone: "UTC", month: "short", day: "numeric" }).format(
    new Date(`${d.slice(0, 10)}T12:00:00Z`),
  );

const STATE_STATUS: Record<MyPayList["weeks"][number]["state"], Status> = {
  paid: "success",
  owes: "critical",
  open: "warning",
  current: "informative",
};

export function MyPay() {
  const { me, company } = useSession();
  const w = payWords(me.locale);
  const day = dateIn(me.locale);
  const list = useMyPay(company.id);
  const [open, setOpen] = useState<string | null>(null);
  if (open) return <MyWeek start={open} onBack={() => setOpen(null)} />;
  const weeks = list.data?.weeks ?? [];
  return (
    <Page title={w.myPay} intro={w.myPayIntro}>
      <ClockCard />
      <ReferCard />
      {list.error ? (
        <LoadError error={list.error} retry={() => void list.refetch()} />
      ) : !list.data ? (
        <Skeleton rows={3} />
      ) : !list.data.linked ? (
        <Banner type="warning">{w.notLinked}</Banner>
      ) : weeks.length === 0 ? (
        <Card>
          <EmptyState icon="money" title={w.noWeeks}>
            {w.noWeeksHint}
          </EmptyState>
        </Card>
      ) : (
        <Card flush>
          <table className="data">
            <tbody>
              {weeks.map((x) => (
                <tr
                  key={x.start}
                  className="clickable"
                  tabIndex={0}
                  onClick={() => setOpen(x.start)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") setOpen(x.start);
                  }}
                >
                  <td>
                    <b>{fillWords(w.week, { date: day(x.start) })}</b>
                    <div className="muted">
                      {x.paidAt ? fillWords(w.paidOn, { date: day(x.paidAt) }) : fillWords(w.payday, { date: day(x.payday) })}
                    </div>
                  </td>
                  <td>
                    <StatusLabel status={STATE_STATUS[x.state]}>{w[x.state]}</StatusLabel>
                  </td>
                  <td className="right num">
                    <b>{moneyExact(Math.abs(x.amountCents))}</b>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </Page>
  );
}

function MyWeek({ start, onBack }: { start: string; onBack: () => void }) {
  const { me, company } = useSession();
  const w = payWords(me.locale);
  const day = dateIn(me.locale);
  const q = useMyPayWeek(company.id, start);
  const d = q.data;
  const why = (l: MyPayAnswer["lines"][number]) =>
    l.kind === "late"
      ? fillWords(w.lateRow, { date: day(l.from ?? "") })
      : l.kind === "change"
        ? fillWords(w.changeRow, { date: day(l.from ?? "") })
        : l.kind === "hours"
          ? fillWords(w.hoursRow, { date: day(l.from ?? "") })
          : null;
  return (
    <Page
      title={fillWords(w.week, { date: day(start) })}
      titleMetaData={
        d ? (
          <>
            {" "}
            <StatusLabel status={STATE_STATUS[d.state]}>{w[d.state]}</StatusLabel>
          </>
        ) : null
      }
      subtitle={
        d ? (d.paidAt ? fillWords(w.paidOn, { date: day(d.paidAt) }) : fillWords(w.payday, { date: day(d.payday) })) : " "
      }
      secondaryAction={
        <Button type="secondary" icon="chevronLeft" onClick={onBack}>
          {w.back}
        </Button>
      }
    >
      {q.error ? (
        <LoadError error={q.error} retry={() => void q.refetch()} />
      ) : !d ? (
        <Skeleton rows={3} />
      ) : (
        <div className="stack">
          {d.state === "owes" || d.amountCents < 0 ? (
            <Banner type="warning">{fillWords(w.owesNote, { amount: moneyExact(-d.amountCents) })}</Banner>
          ) : null}
          {d.owedCents > 0 ? (
            <Banner type="notice">
              {fillWords(w.owedNote, { amount: moneyExact(d.owedCents), date: day(d.owedFrom ?? start) })}
            </Banner>
          ) : null}
          {d.nextStart ? <Banner type="success">{fillWords(w.frozen, { date: day(d.nextStart) })}</Banner> : null}
          <Card title={w.jobs} flush>
            {d.lines.length ? (
              <table className="data">
                <tbody>
                  {d.lines.map((l, i) => (
                    <tr key={i}>
                      <td>{l.date ? day(l.date) : ""}</td>
                      <td>
                        {l.clientName ?? w.hours}
                        {why(l) ? <div className="muted">{why(l)}</div> : null}
                        {l.cash ? <div className="muted">{w.cash}</div> : null}
                      </td>
                      <td className="right num">{moneyExact(l.payCents)}</td>
                      <td className="right num muted">{l.tipCents ? `+ ${moneyExact(l.tipCents)}` : ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="card-empty muted">{w.noLines}</div>
            )}
          </Card>
          <Card flush>
            <table className="data">
              <tbody>
                <tr>
                  <td>{d.payType === "hourly" ? w.hourlyPay : w.jobPay}</td>
                  <td className="right num">{moneyExact(d.payType === "hourly" ? (d.hourlyPayCents ?? 0) : d.jobPayCents)}</td>
                </tr>
                {d.payType === "hourly" && d.jobPayCents ? (
                  <tr>
                    <td>{w.cash}</td>
                    <td className="right num">{moneyExact(d.jobPayCents)}</td>
                  </tr>
                ) : null}
                <tr>
                  <td>{w.tips}</td>
                  <td className="right num">{moneyExact(d.tipCents)}</td>
                </tr>
                <tr>
                  <td>{w.hours}</td>
                  <td className="right num">{d.minutes ? minutesWords(d.minutes) : "0"}</td>
                </tr>
                {d.owedCents ? (
                  <tr>
                    <td>{w.owedBefore}</td>
                    <td className="right num">{moneyExact(-d.owedCents)}</td>
                  </tr>
                ) : null}
                <tr>
                  <td>
                    <b>{w.total}</b>
                  </td>
                  <td className="right num">
                    <b>{moneyExact(d.amountCents)}</b>
                  </td>
                </tr>
              </tbody>
            </table>
          </Card>
        </div>
      )}
    </Page>
  );
}

/** Clock in and out for the signed-in person. Hidden when their login is not on the team list. */
export function ClockCard() {
  const { me, company } = useSession();
  const w = payWords(me.locale);
  const clock = useClock(company.id);
  const press = usePressClock();
  const toast = useToast();
  const c = clock.data;
  if (!c?.linked) return null;
  const go = () =>
    press.mutate(c.on ? "out" : "in", {
      onSuccess: (a) => toast(a.on ? w.clockedIn : w.clockedOut),
      onError: (e) => toast(e instanceof ApiError ? e.message : "That didn't save. Try again.", { kind: "error" }),
    });
  return (
    <Card>
      <div className="row" style={{ justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <b>{c.on && c.shift ? fillWords(w.onClock, { time: c.shift.inTime }) : w.offClock}</b>
          <div className="muted">{fillWords(w.today, { hours: minutesWords(c.todayMinutes) })}</div>
        </div>
        <Button icon="clock" type={c.on ? "secondary" : "primary"} loading={press.isPending} onClick={go}>
          {c.on ? w.clockOut : w.clockIn}
        </Button>
      </div>
    </Card>
  );
}

/** Refer a friend (Hiring v2 phase 2): their own link to the careers page, so the board shows who sent them. */
function ReferCard() {
  const { me, company } = useSession();
  const r = useReferLink(company.id);
  const toast = useToast();
  if (!r.data?.link) return null;
  const link = r.data.link;
  const es = me.locale === "es";
  const bonus = r.data.bonusCents > 0 ? moneyExact(r.data.bonusCents) : null;
  return (
    <Card title={es ? "Recomienda a un amigo" : "Refer a friend"}>
      <p className="muted">
        {es
          ? `Manda este enlace a alguien bueno que quiera trabajar con nosotros.${bonus ? ` Si lo contratamos y se queda ${String(r.data.afterDays)} días, te damos ${bonus}.` : ""}`
          : `Send this link to someone good who wants to work with us.${bonus ? ` If we hire them and they stay ${String(r.data.afterDays)} days, you get ${bonus}.` : ""}`}
      </p>
      <div className="row wrap">
        <Button
          type="secondary"
          onClick={() => {
            const done = () => toast(es ? "Enlace copiado" : "Link copied");
            if (typeof navigator.share === "function")
              void navigator.share({ url: link }).catch(() => navigator.clipboard.writeText(link).then(done));
            else void navigator.clipboard.writeText(link).then(done);
          }}
        >
          {es ? "Compartir mi enlace" : "Share my link"}
        </Button>
      </div>
    </Card>
  );
}
